import { Settings2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { DefaultsFooter, FilterChip, SelectionActions, type FilterDefaults } from './FilterSelectionControls'

const ALL_MARKETS = ['spot', 'futures']

type FilterSelection = {
  exchanges: string[]
  markets: string[]
}

export interface ScreenerFilterControlsProps {
  includeOrderbook?: boolean
  availableExchanges: string[]
  selectedExchanges: string[]
  onSelectedExchangesChange: (exchanges: string[]) => void
  chartExchangeOptions: string[]
  orderbookExchangeOptions: string[]
  chartFilters: FilterSelection
  orderbookFilters: FilterSelection
  onParseFiltersChange: (next: {
    chartExchanges?: string[]
    chartMarkets?: string[]
    orderbookExchanges?: string[]
    orderbookMarkets?: string[]
  }) => Promise<unknown>
  parseDisabled: boolean
  displayDefaults: FilterDefaults
  parseDefaults: FilterDefaults
}

function toggleSelection(selected: string[], value: string): string[] {
  return selected.includes(value)
    ? selected.filter((item) => item !== value)
    : [...selected, value]
}

function ParseFilterSection({
  label,
  exchangeOptions,
  filters,
  disabled,
  onExchangesChange,
  onMarketsChange,
}: {
  label: string
  exchangeOptions: string[]
  filters: FilterSelection
  disabled: boolean
  onExchangesChange: (exchanges: string[]) => void
  onMarketsChange: (markets: string[]) => void
}) {
  return (
    <section className="space-y-2" aria-label={`${label} parse filters`}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase text-muted-foreground">{label} Parse Filters</h3>
        <SelectionActions
          label={`${label.toLowerCase()} parse exchanges`}
          disabled={disabled}
          onAll={() => onExchangesChange([...exchangeOptions])}
          onNone={() => onExchangesChange([])}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label={`${label} markets`}>
        <span className="text-[11px] text-muted-foreground">Markets</span>
        {ALL_MARKETS.map((market) => (
          <FilterChip
            key={market}
            value={market}
            selected={filters.markets.includes(market)}
            disabled={disabled}
            uppercase
            onClick={() => onMarketsChange(toggleSelection(filters.markets, market))}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={`${label} exchanges`}>
        {exchangeOptions.map((exchange) => (
          <FilterChip
            key={exchange}
            value={exchange}
            selected={filters.exchanges.includes(exchange)}
            disabled={disabled}
            onClick={() => onExchangesChange(toggleSelection(filters.exchanges, exchange))}
          />
        ))}
      </div>
    </section>
  )
}

export function ScreenerFilterControls({
  includeOrderbook = true,
  availableExchanges,
  selectedExchanges,
  onSelectedExchangesChange,
  chartExchangeOptions,
  orderbookExchangeOptions,
  chartFilters,
  orderbookFilters,
  onParseFiltersChange,
  parseDisabled,
  displayDefaults,
  parseDefaults,
}: ScreenerFilterControlsProps) {
  const parseControlsDisabled = parseDisabled || parseDefaults.saving

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 text-xs border-border/50 bg-muted/10 gap-2">
            <Settings2 className="h-3 w-3" />
            <span>Exchanges</span>
            <Badge variant="secondary" className="h-4 px-1 text-[9px] bg-muted text-foreground hover:bg-muted">
              {selectedExchanges.filter((exchange) => availableExchanges.includes(exchange)).length}
            </Badge>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 max-w-[calc(100vw-1rem)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-3" align="start">
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-semibold uppercase text-muted-foreground">Display Sources</h3>
              <SelectionActions
                label="display exchanges"
                disabled={displayDefaults.saving}
                onAll={() => onSelectedExchangesChange([...availableExchanges])}
                onNone={() => onSelectedExchangesChange([])}
              />
            </div>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Display exchanges">
              {availableExchanges.map((exchange) => (
                <FilterChip
                  key={exchange}
                  value={exchange}
                  selected={selectedExchanges.includes(exchange)}
                  disabled={displayDefaults.saving}
                  onClick={() => onSelectedExchangesChange(toggleSelection(selectedExchanges, exchange))}
                />
              ))}
            </div>
            <DefaultsFooter label="display" defaults={displayDefaults} />
          </div>
        </PopoverContent>
      </Popover>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 text-xs border-border/50 bg-muted/10 gap-2">
            <Settings2 className="h-3 w-3" />
            <span>Parse</span>
            <Badge variant="secondary" className="h-4 px-1 text-[9px] bg-muted text-foreground hover:bg-muted">
              {chartFilters.exchanges.length}{includeOrderbook && `/${orderbookFilters.exchanges.length}`}
            </Badge>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] max-w-[calc(100vw-1rem)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto p-3" align="start">
          <div className="space-y-4">
            <ParseFilterSection
              label="Charts"
              exchangeOptions={chartExchangeOptions}
              filters={chartFilters}
              disabled={parseControlsDisabled}
              onExchangesChange={(exchanges) => void onParseFiltersChange({ chartExchanges: exchanges })}
              onMarketsChange={(markets) => void onParseFiltersChange({ chartMarkets: markets })}
            />
            {includeOrderbook && <ParseFilterSection
              label="Orderbook"
              exchangeOptions={orderbookExchangeOptions}
              filters={orderbookFilters}
              disabled={parseControlsDisabled}
              onExchangesChange={(exchanges) => void onParseFiltersChange({ orderbookExchanges: exchanges })}
              onMarketsChange={(markets) => void onParseFiltersChange({ orderbookMarkets: markets })}
            />}
            <DefaultsFooter label="parse" defaults={parseDefaults} disabled={parseDisabled} />
          </div>
        </PopoverContent>
      </Popover>
    </>
  )
}
